import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';

interface FoodScanningModalProps {
  isOpen: boolean;
  imageUrl: string;
}

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: ${theme.zIndex.modal};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  width: 100%;
  max-width: 375px;
  padding: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xl};
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 24px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
  text-align: center;
`;

const ImageContainer = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 16px;
  overflow: hidden;
  animation: ${pulse} 2s ease-in-out infinite;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const FoodScanningModal: React.FC<FoodScanningModalProps> = ({ isOpen, imageUrl }) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  return (
    <Overlay $isOpen={isOpen}>
      <Container
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="food-scanning-title"
        tabIndex={-1}
      >
        <Title id="food-scanning-title">{t('food.scanning')}</Title>
        <Subtitle>{t('food.scanningSubtitle')}</Subtitle>
        <ImageContainer>
          <img src={imageUrl} alt="Food being scanned" />
        </ImageContainer>
      </Container>
    </Overlay>
  );
};
